// In-app offline listening: a sound saved for offline is kept inside Pipaan (the browser's own app storage), never
// handed to the user as a file. The Pipaan player plays this copy instead of streaming it. The copy expires after
// OFFLINE_DAYS, and the Sound Detail page removes it as soon as the listener no longer has permission to keep it.

const DB_NAME = "pipaan-offline";
const STORE = "sounds";
export const OFFLINE_DAYS = 30;

const ids = new Set(); // sounds that currently have an offline copy (kept in memory so the player can ask synchronously)
const listeners = new Set();
let ready = null;

export const isOfflineSupported = () => typeof indexedDB !== "undefined";

const notify = () => listeners.forEach((fn) => { try { fn(); } catch (e) { /* a listener must never break storage */ } });
export const subscribeOffline = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

function open() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => { if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: "id" }); };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

const run = (mode, work) =>
  open().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const out = work(tx.objectStore(STORE));
    tx.oncomplete = () => { db.close(); resolve(out && "result" in out ? out.result : undefined); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error); };
  }));

// Load which sounds are saved (and drop expired copies). Safe to call more than once.
export function initOffline() {
  if (!isOfflineSupported()) return Promise.resolve();
  if (!ready) {
    ready = run("readwrite", (store) => {
      const req = store.getAll();
      req.onsuccess = () => {
        req.result.forEach((row) => {
          if (row.expires_at && row.expires_at < Date.now()) store.delete(row.id);
          else ids.add(row.id);
        });
      };
      return req;
    }).then(notify).catch(() => { ready = null; });
  }
  return ready;
}
initOffline();

export const hasOffline = (id) => ids.has(Number(id));

export async function saveOffline(id, blob, meta = {}) {
  if (!isOfflineSupported()) throw new Error("Offline listening is not supported in this browser.");
  const row = { id: Number(id), blob, meta, saved_at: Date.now(), expires_at: Date.now() + OFFLINE_DAYS * 86400000 };
  await run("readwrite", (store) => store.put(row));
  ids.add(row.id);
  notify();
}

export async function removeOffline(id) {
  const key = Number(id);
  ids.delete(key);
  if (isOfflineSupported()) await run("readwrite", (store) => store.delete(key)).catch(() => {});
  notify();
}

// What is saved for offline listening on this browser, newest first (expired copies are dropped). Used by My Library > Downloads.
export async function listOffline() {
  if (!isOfflineSupported()) return [];
  const rows = (await run("readonly", (store) => store.getAll()).catch(() => [])) || [];
  const now = Date.now();
  rows.filter((r) => r.expires_at && r.expires_at < now).forEach((r) => removeOffline(r.id));
  return rows
    .filter((r) => !(r.expires_at && r.expires_at < now))
    .sort((a, b) => (b.saved_at || 0) - (a.saved_at || 0))
    .map((r) => ({ id: r.id, meta: r.meta || {}, saved_at: r.saved_at, expires_at: r.expires_at }));
}

// A playable address for the saved copy, or null when there is none (or it has expired).
export async function offlineUrl(id) {
  if (!isOfflineSupported() || !hasOffline(id)) return null;
  const row = await run("readonly", (store) => store.get(Number(id))).catch(() => null);
  if (!row || !row.blob) { ids.delete(Number(id)); notify(); return null; }
  if (row.expires_at && row.expires_at < Date.now()) { await removeOffline(id); return null; }
  return URL.createObjectURL(row.blob);
}
